// What a share-link recipient sees. No account, no login, no chrome from
// the app — just the thing they were sent, read-only.
//
//   /shared/areas/:token?g=…    the project's approved coordination areas
//   /shared/sheet/:token?g=…    one Document Control sheet
//   /shared/schedule/:token?g=… the rows THEIR company is responsible
//                               for - not the whole plan (see the route)
//
// Both live in one file because they are the same page with a different
// body: the same token handling, the same "who are you" step, the same
// read-only framing. The API answers { needsIdentity: true } when the
// link was forwarded and the visitor has no personal token — that is not
// an error, it is the registration step, and it mirrors the issue share
// flow's GuestRegistrationModal.
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Input, Button, Spin, Alert, Tag } from "antd";
import { LockOutlined, FileTextOutlined } from "@ant-design/icons";
import moment from "moment";
import { url } from "api";
import { T } from "pages/Issue/theme";
import AreaShapePreview from "components/Coordination/AreaShapePreview";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// The guest token rides in the URL. Kept per (kind, token) in
// sessionStorage so a refresh after the identity step doesn't ask again,
// and so two shares open in two tabs don't overwrite each other.
const keyFor = (kind, token) => `bl_share_g:${kind}:${token}`;

export default function SharedResourcePage({ kind }) {
  const { token } = useParams();
  const location = useLocation();
  const fromUrl = new URLSearchParams(location.search).get("g") || "";

  const [guestToken, setGuestToken] = useState(() => {
    if (fromUrl) return fromUrl;
    try { return sessionStorage.getItem(keyFor(kind, token)) || ""; } catch (e) { return ""; }
  });
  const [state, setState] = useState({ status: "loading", data: null, err: "" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!fromUrl) return;
    try { sessionStorage.setItem(keyFor(kind, token), fromUrl); } catch (e) { /* private mode */ }
  }, [fromUrl, kind, token]);

  const load = useCallback(async () => {
    setState({ status: "loading", data: null, err: "" });
    try {
      const qs = guestToken ? `?g=${encodeURIComponent(guestToken)}` : "";
      const r = await fetch(`${url}/shared/${kind}/${token}${qs}`);
      const b = await r.json();
      if (!r.ok) { setState({ status: "gone", data: null, err: b.err || "This link is no longer available" }); return; }
      if (b.needsIdentity) { setState({ status: "identify", data: null, err: "" }); return; }
      setState({ status: "ready", data: b, err: "" });
    } catch (e) {
      setState({ status: "gone", data: null, err: "Could not open this link" });
    }
  }, [kind, token, guestToken]);

  useEffect(() => { load(); }, [load]);

  const identify = async () => {
    if (!name.trim() || name.trim().length < 2) return;
    if (!EMAIL_RE.test(email.trim())) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${url}/shared/${kind}/${token}/identify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase() }),
      });
      const b = await r.json();
      if (!r.ok) { setState({ status: "gone", data: null, err: b.err || "Could not open this link" }); return; }
      try { sessionStorage.setItem(keyFor(kind, token), b.guestToken); } catch (e) { /* private mode */ }
      setGuestToken(b.guestToken);   // triggers load() through the callback dep
    } catch (e) {
      setState({ status: "gone", data: null, err: "Could not open this link" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "calc(100 * var(--vh))", background: T.bg }}>
      <div style={{
        background: "#fff", borderBottom: `1px solid ${T.line}`,
        padding: "14px 24px", display: "flex", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontWeight: 700, color: T.navyD, fontSize: 16, letterSpacing: "-.01em" }}>BidLight</span>
        <Tag icon={<LockOutlined />} style={{ background: T.wash, borderColor: "#D4E3EC", color: T.navyD, margin: 0 }}>
          Read-only
        </Tag>
        <span style={{ flex: 1 }} />
        {state.data?.viewer && (
          <span style={{ fontSize: 12.5, color: T.muted }}>
            Signed in as {state.data.viewer.name}
          </span>
        )}
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 64px" }}>
        {state.status === "loading" && (
          <div style={{ textAlign: "center", padding: "80px 0" }}><Spin size="large" /></div>
        )}

        {state.status === "gone" && (
          <Alert type="error" showIcon style={{ maxWidth: 520, margin: "60px auto" }}
                 message="This link is no longer available"
                 description={<span>{state.err} If you still need access, ask whoever shared it to send you a new link.</span>} />
        )}

        {state.status === "identify" && (
          <div style={{
            maxWidth: 420, margin: "60px auto", background: "#fff",
            border: `1px solid ${T.line}`, borderRadius: 8, padding: 26,
          }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 19, color: T.ink }}>Who's opening this?</h2>
            <p style={{ fontSize: 13, color: T.muted, marginBottom: 18 }}>
              This link was shared with a specific person. Tell us who you are and it will open —
              no account and no password needed.
            </p>
            <Input placeholder="Your name" value={name} size="large" style={{ marginBottom: 10 }}
                   onChange={(e) => setName(e.target.value)} autoFocus />
            <Input placeholder="Your email" type="email" value={email} size="large" style={{ marginBottom: 16 }}
                   onChange={(e) => setEmail(e.target.value)} onPressEnter={identify} />
            <Button type="primary" size="large" block loading={submitting} onClick={identify}
                    disabled={name.trim().length < 2 || !EMAIL_RE.test(email.trim())}
                    style={{ background: T.orange, borderColor: T.orange }}>
              Open
            </Button>
          </div>
        )}

        {state.status === "ready" && kind === "areas" && <AreasBody data={state.data} />}
        {state.status === "ready" && kind === "sheet" && <SheetBody data={state.data} />}
        {state.status === "ready" && kind === "schedule" && <ScheduleBody data={state.data} />}
      </div>
    </div>
  );
}

function AreasBody({ data }) {
  const areas = data.areas || [];
  return (
    <>
      <h1 style={{ fontSize: 26, color: T.ink, margin: "0 0 4px", fontWeight: 700 }}>
        {data.project?.title || "Levels & Areas"}
      </h1>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13.5 }}>
        {areas.length} area{areas.length === 1 ? "" : "s"}
        {data.project?.projectNumber ? ` · ${data.project.projectNumber}` : ""}
      </p>

      {areas.length === 0 ? (
        <Alert type="info" showIcon message="No areas have been published on this project yet." />
      ) : (
        <div style={{
          display: "grid", gap: 14,
          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        }}>
          {areas.map((a) => (
            <div key={a._id} style={{
              background: "#fff", border: `1px solid ${T.line}`,
              borderRadius: 8, padding: 14, overflow: "hidden",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{
                  width: 11, height: 11, borderRadius: 3,
                  background: a.color || T.navy, flexShrink: 0,
                }} />
                <span style={{
                  fontWeight: 600, color: T.ink, fontSize: 14,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{a.name}</span>
              </div>
              {a.points?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <AreaShapePreview area={a} zoom width={220} height={150} />
                </div>
              )}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {a.code ? <Tag style={{ margin: 0 }}>{a.code}</Tag> : null}
                {a.level ? <Tag style={{ margin: 0 }}>{a.level}</Tag> : null}
                {a.discipline ? <Tag style={{ margin: 0 }}>{a.discipline}</Tag> : null}
                {a.kind ? <Tag style={{ margin: 0 }}>{a.kind}</Tag> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


// The recipient's own portion of the plan, as a dated list with a bar
// each. Deliberately NOT the whole schedule: see GET /shared/schedule.
//
// It is drawn against the WHOLE project's span rather than against the
// recipient's own earliest and latest date, because "my work sits here,
// in this job" is the thing a subcontractor is trying to read, and a
// chart scaled to their own rows alone always looks full.
function ScheduleBody({ data }) {
  const items = data.items || [];
  const span = data.span || null;
  const t0 = span?.start ? new Date(span.start).getTime() : null;
  const t1 = span?.end ? new Date(span.end).getTime() : null;
  const total = t0 && t1 && t1 > t0 ? t1 - t0 : 0;
  const pct = (d) => {
    if (!total || !d) return null;
    const v = ((new Date(d).getTime() - t0) / total) * 100;
    return Math.max(0, Math.min(100, v));
  };
  const fmt = (d) => (d ? moment(d).format("D MMM YYYY") : "—");
  const late = (it) => it.endDate && moment(it.endDate).isBefore(moment(), "day") && (it.progress || 0) < 100;

  return (
    <>
      <h1 style={{ fontSize: 26, color: T.ink, margin: "0 0 4px", fontWeight: 700 }}>
        {data.project?.title || "Schedule"}
      </h1>
      <p style={{ color: T.muted, marginBottom: 20, fontSize: 13.5 }}>
        {data.company?.name ? `${data.company.name} · ` : ""}
        {items.length} activit{items.length === 1 ? "y" : "ies"}
        {data.project?.projectNumber ? ` · ${data.project.projectNumber}` : ""}
      </p>

      {/* Why the list is short, said out loud. Somebody who expected the
          whole programme should not be left wondering if it is broken.
          Keyed on what is actually HERE, not just on the roster: being on
          the roster and owning no rows is the common case on a project
          that has not been carved up yet, and the old wording announced
          "your company's activities" and then showed nothing - which is
          what made a working link read as broken. */}
      <Alert
        type="info" showIcon style={{ marginBottom: 18 }}
        message={items.length > 0
          ? "Your company's activities"
          : data.onRoster
            ? `No activities are assigned to ${data.company?.name || "your company"} yet`
            : "Nothing is assigned to you on this project yet"}
        description={items.length > 0
          ? "This page shows the activities your company is responsible for. The rest of the programme is not shown."
          : data.onRoster
            ? "You are on this project's roster, but none of the programme has been assigned to your company yet. Nothing is missing from this page - there is simply nothing on it yet. Ask the project team to assign your scope, and it will appear here."
            : "This link shows the activities your company is responsible for. None have been assigned to your address yet - ask the project team to add you to the roster."}
      />

      {items.length === 0 && (
        <div style={{
          background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
          padding: "36px 24px", textAlign: "center", color: T.muted,
        }}>
          <div style={{ fontSize: 15, color: T.ink, marginBottom: 6 }}>
            Nothing on the schedule yet
          </div>
          <div style={{ fontSize: 13 }}>
            {data.project?.title ? `${data.project.title} has a programme, ` : "This project has a programme, "}
            but none of it is assigned to {data.company?.name || "your company"} at the moment.
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, overflow: "hidden" }}>
          {items.map((it, i) => {
            const left = pct(it.startDate);
            const right = pct(it.endDate);
            const width = left != null && right != null ? Math.max(1.5, right - left) : null;
            return (
              <div key={it._id} style={{
                padding: "12px 14px",
                borderTop: i ? `1px solid ${T.line}` : "none",
              }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: T.ink, fontSize: 14 }}>{it.name || "Activity"}</span>
                  {it.wbs ? <Tag style={{ margin: 0 }}>{it.wbs}</Tag> : null}
                  {late(it) ? <Tag color="red" style={{ margin: 0 }}>overdue</Tag> : null}
                  <span style={{ marginLeft: "auto", color: T.muted, fontSize: 12.5 }}>
                    {fmt(it.startDate)} → {fmt(it.endDate)}
                  </span>
                </div>
                {width != null && (
                  <div style={{ position: "relative", height: 10, borderRadius: 5, background: "#EEF1F4", marginTop: 8 }}>
                    <div style={{
                      position: "absolute", left: `${left}%`, width: `${width}%`, top: 0, bottom: 0,
                      borderRadius: 5, background: late(it) ? "#D64545" : T.navy, opacity: 0.85,
                    }} />
                  </div>
                )}
                <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 12, color: T.muted }}>
                  <span>{it.progress || 0}% complete</span>
                  {it.status ? <span>· {String(it.status).replace(/_/g, " ")}</span> : null}
                  {it.responsible ? <span>· {it.responsible}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function SheetBody({ data }) {
  const s = data.sheet || {};
  // A shared document is routinely several sheets. The API renders every
  // page at upload time and hands them over in order, so the recipient
  // flips through them here rather than seeing page one and assuming
  // that is the whole document.
  const pages = Array.isArray(s.pages) ? s.pages.filter(Boolean) : [];
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [s.sheetNumber, s.sheetName]);
  // A sheet's `link` may be a PDF or an image; the API hands over
  // whatever Document Control stored, and relative paths resolve against
  // the API origin the same way they do inside the app.
  const abs = (p) => (p && p.startsWith("/") ? `${url}${p}` : p);
  const href = abs(s.link);
  // Rendered pages win over the raw file: they are images, so they paint
  // without a PDF plugin and they can be paged through.
  const current = pages.length ? abs(pages[Math.min(page, pages.length - 1)]) : null;
  const isPdf = !pages.length && /\.pdf(\?|$)/i.test(s.link || "");
  const isImg = !pages.length && /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(s.link || "");

  const Row = ({ k, v }) => (v ? (
    <div style={{ display: "flex", gap: 10, fontSize: 13, padding: "5px 0" }}>
      <span style={{ color: T.muted, minWidth: 92 }}>{k}</span>
      <span style={{ color: T.ink }}>{v}</span>
    </div>
  ) : null);

  return (
    <>
      <h1 style={{ fontSize: 24, color: T.ink, margin: "0 0 4px", fontWeight: 700 }}>
        {s.sheetNumber ? `${s.sheetNumber} — ` : ""}{s.sheetName}
      </h1>
      <p style={{ color: T.muted, marginBottom: 22, fontSize: 13.5 }}>
        {s.projectTitle}
      </p>

      <div style={{ display: "grid", gap: 18, gridTemplateColumns: "minmax(0,1fr) 260px", alignItems: "start" }}>
        <div style={{
          background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
          overflow: "hidden", minHeight: 420,
        }}>
          {pages.length > 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
              padding: "9px 12px", borderBottom: `1px solid ${T.line}`, background: T.bg,
            }}>
              <Button size="small" disabled={page === 0} onClick={() => setPage((n) => Math.max(0, n - 1))}>
                Previous
              </Button>
              <span style={{ fontSize: 12.5, color: T.text, minWidth: 96, textAlign: "center" }}>
                Sheet {page + 1} of {pages.length}
              </span>
              <Button size="small" disabled={page >= pages.length - 1}
                      onClick={() => setPage((n) => Math.min(pages.length - 1, n + 1))}>
                Next
              </Button>
            </div>
          )}
          {current ? (
            <img src={current} alt={`${s.sheetName} - sheet ${page + 1}`}
                 style={{ width: "100%", display: "block" }} />
          ) : isImg ? (
            <img src={href} alt={s.sheetName} style={{ width: "100%", display: "block" }} />
          ) : isPdf ? (
            <iframe title={s.sheetName} src={href} style={{ width: "100%", height: "calc(72 * var(--vh))", border: "none" }} />
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: T.muted }}>
              <FileTextOutlined style={{ fontSize: 34, marginBottom: 10 }} />
              <div style={{ fontSize: 13 }}>This file can't be previewed in the browser.</div>
            </div>
          )}
        </div>

        <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 11, letterSpacing: ".07em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>
            Sheet details
          </div>
          <Row k="Number" v={s.sheetNumber} />
          <Row k="Discipline" v={s.discipline} />
          <Row k="Revision" v={s.revisionNumber != null ? String(s.revisionNumber) : ""} />
          <Row k="Revised" v={s.revisionDate ? moment(s.revisionDate).format("MMM D, YYYY") : ""} />
          <Row k="Level" v={(s.floorLevel || []).join(", ")} />
          <Row k="Status" v={s.sheetStatus} />
          <Row k="Sheets" v={pages.length > 1 ? `${pages.length} pages` : ""} />
          {s.description ? (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.line}`, fontSize: 13, color: T.text }}>
              {s.description}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
